/**
 * "Track keyword" CTA — POSTs to /v1/tracked-keywords.
 *
 * Phase 1 keyword-picker refactor — the previous Select + free-text pair is
 * replaced with a single shared KeywordPicker. When the user picks from a
 * completed keyword run, we forward `keyword_id` + `keyword_run_id` so the
 * tracked row is linked back to the source (used by future keyword_lists,
 * cross-linking with briefs, etc.). Free-text entry still works — those
 * columns stay NULL.
 */
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { PlusCircle, Radar } from 'lucide-react';
import { toast } from 'sonner';
import type { Project, TrackedKeyword } from '@seo/types';
import { Button, type ButtonProps } from '@/components/ui/button';
import { FormDialog, FormDialogBlock } from '@/components/ui/form-dialog';
import { TextField, FormField } from '@/components/ui/form-field';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { KeywordPicker, type KeywordPickerValue } from '@/components/keyword-picker';
import { extractFieldErrors, summarizeApiError } from '@/lib/api';
import { useCreateTrackedKeyword } from '../use-create-tracked-keyword';

const FormSchema = z.object({
  project_id: z.string().uuid({ message: 'Pick a project' }),
  keyword: z.string().trim().min(1, 'Type the keyword you want to watch').max(200),
  target_url: z
    .string()
    .trim()
    .url('Must be a full URL')
    .max(2000)
    .optional()
    .or(z.literal('')),
});
type FormValues = z.infer<typeof FormSchema>;

export interface TrackKeywordButtonProps {
  projects: Project[];
  defaultProjectId?: string;
  defaultKeyword?: string;
  /**
   * Phase 1 keyword-picker — when opened from a keyword-run row, forwards
   * the picked-keyword IDs so tracking inherits `keyword_id` +
   * `keyword_run_id`.
   */
  defaultKeywordId?: string;
  defaultKeywordRunId?: string;
  label?: string;
  buttonProps?: Omit<ButtonProps, 'onClick'>;
  onCreated?: (row: TrackedKeyword) => void;
}

export function TrackKeywordButton({
  projects,
  defaultProjectId,
  defaultKeyword,
  defaultKeywordId,
  defaultKeywordRunId,
  label = 'Track keyword',
  buttonProps,
  onCreated,
}: TrackKeywordButtonProps): JSX.Element {
  const [open, setOpen] = useState(false);
  const trigger = (
    <Button {...buttonProps} onClick={() => setOpen(true)}>
      <PlusCircle className="h-4 w-4" />
      {label}
    </Button>
  );

  if (projects.length === 0) {
    return (
      <FormDialogBlock
        open={open}
        onOpenChange={setOpen}
        trigger={trigger}
        title="No projects yet"
        description="Create a project first — tracked keywords live inside a project."
      />
    );
  }

  return (
    <>
      {trigger}
      {open ? (
        <TrackDialog
          open={open}
          onOpenChange={setOpen}
          projects={projects}
          defaultProjectId={defaultProjectId}
          defaultKeyword={defaultKeyword}
          defaultKeywordId={defaultKeywordId}
          defaultKeywordRunId={defaultKeywordRunId}
          onCreated={onCreated}
        />
      ) : null}
    </>
  );
}

function TrackDialog({
  open,
  onOpenChange,
  projects,
  defaultProjectId,
  defaultKeyword,
  defaultKeywordId,
  defaultKeywordRunId,
  onCreated,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  projects: Project[];
  defaultProjectId?: string;
  defaultKeyword?: string;
  defaultKeywordId?: string;
  defaultKeywordRunId?: string;
  onCreated?: (row: TrackedKeyword) => void;
}): JSX.Element {
  const create = useCreateTrackedKeyword();

  const form = useForm<FormValues>({
    resolver: zodResolver(FormSchema),
    defaultValues: {
      project_id: defaultProjectId ?? projects[0]?.id ?? '',
      keyword: defaultKeyword ?? '',
      target_url: '',
    },
  });

  const projectId = form.watch('project_id');

  const initialPickerValue: KeywordPickerValue | null = defaultKeyword
    ? defaultKeywordId && defaultKeywordRunId
      ? {
          kind: 'picked',
          keywordId: defaultKeywordId,
          keywordRunId: defaultKeywordRunId,
          term: defaultKeyword,
          volume: null,
          difficulty: null,
          intent: null,
          clusterLabel: null,
        }
      : { kind: 'free-text', term: defaultKeyword }
    : null;
  const [pickerValue, setPickerValue] = useState<KeywordPickerValue | null>(initialPickerValue);

  useEffect(() => {
    if (!open) return;
    form.reset({
      project_id: defaultProjectId ?? projects[0]?.id ?? '',
      keyword: defaultKeyword ?? '',
      target_url: '',
    });
    setPickerValue(initialPickerValue);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, defaultProjectId, defaultKeyword, defaultKeywordId, defaultKeywordRunId, projects]);

  const onSubmit = form.handleSubmit(async (values) => {
    try {
      const row = await create.mutateAsync({
        project_id: values.project_id,
        keyword: values.keyword,
        target_url: values.target_url ? values.target_url : null,
        ...(pickerValue?.kind === 'picked'
          ? {
              keyword_id: pickerValue.keywordId,
              keyword_run_id: pickerValue.keywordRunId,
            }
          : {}),
      });
      toast.success(`Now watching "${row.keyword}"`, {
        description:
          'Next we look up your Google position. You\'ll see a rank (#) and later 7-day / 30-day changes on Tracking.',
      });
      onCreated?.(row);
      onOpenChange(false);
    } catch (e) {
      const fields = extractFieldErrors(e);
      for (const [k, msg] of Object.entries(fields)) {
        form.setError(k as keyof FormValues, { message: msg });
      }
      if (Object.keys(fields).length === 0) {
        const summary = summarizeApiError(e, {
          fallbackTitle: 'Could not track keyword',
          fieldLabels: {
            keyword: 'Keyword',
            project_id: 'Project',
            target_url: 'Target URL',
          },
        });
        toast.error(summary.title, { description: summary.description });
      }
    }
  });

  return (
    <FormDialog
      open={open}
      onOpenChange={onOpenChange}
      trigger={<span />}
      title="Track a keyword"
      description="You choose the exact search term — we never invent one. Outcome: your site's Google position for that term, tracked over time."
      icon={<Radar className="h-5 w-5" />}
      onSubmit={onSubmit}
      submitting={create.isPending}
      submitLabel="Start watching"
    >
      <FormField label="Project" error={form.formState.errors.project_id?.message}>
        <Select
          value={form.watch('project_id')}
          onValueChange={(v) => form.setValue('project_id', v, { shouldValidate: true })}
        >
          <SelectTrigger>
            <SelectValue placeholder="Pick a project" />
          </SelectTrigger>
          <SelectContent>
            {projects.map((p) => (
              <SelectItem key={p.id} value={p.id}>
                {p.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </FormField>

      <FormField
        label="Keyword to watch"
        error={form.formState.errors.keyword?.message}
        hint={
          pickerValue?.kind === 'picked'
            ? 'Picked from your research — this keeps the tracked row linked to the source run.'
            : 'Search across your keyword research, or type your own exact query.'
        }
      >
        <KeywordPicker
          projectId={projectId || undefined}
          value={pickerValue}
          onChange={(v) => {
            setPickerValue(v);
            form.setValue('keyword', v.term, { shouldValidate: true, shouldDirty: true });
          }}
          autoFocus={!defaultKeyword}
          disabled={!projectId}
          placeholder="e.g. best project management software"
        />
      </FormField>

      <TextField
        label="Page to match (optional)"
        placeholder="https://example.com/blog/post"
        error={form.formState.errors.target_url?.message}
        hint="Blank = any page on your domain. Set a URL to only count that page."
        {...form.register('target_url')}
      />
    </FormDialog>
  );
}
